import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { AdFormUI } from "./AdFormUI";

import { previewFun } from "../utils/previewFun";
import { publishFun } from "../utils/publishFun";
import { tagsFun } from "../utils/tagsFun";
import { phoneFun } from "../utils/phoneFun";
import { changesFun } from "../utils/changesFun";
import { getUserData } from "../utils/getUserData";
import { t } from "i18next";

import { getFromDB } from "../../../utils/indexedDBService";

function AdForm() {
    const navigate = useNavigate();
    const [priceType, setPriceType] = useState(localStorage.getItem("priceType") || "fixed");
    const [announcementName, setAnnouncementName] = useState(localStorage.getItem("announcementName" || ""));
    const [videoLink, setVideoLink] = useState(localStorage.getItem("videoLink") || "");
    const [description, setDescription] = useState(localStorage.getItem("description") || "");
    const [price, setPrice] = useState(localStorage.getItem("price") || "");
    const [selectedCategory, setSelectedCategory] = useState(() => {
        return localStorage.getItem("selectedCategory") || "";
    });

    const [selectedSubcategory, setSelectedSubcategory] = useState(() => {
        return localStorage.getItem("selectedSubcategory") || "";
    });

    const [selectedLocation, setSelectedLocation] = useState(() => {
        const savedLocation = localStorage.getItem("selectedLocation");
        return savedLocation ? JSON.parse(savedLocation) : "";
    });
    const [images, setImages] = useState([]);

    useEffect(() => {
        const fetchImages = async () => {
            const savedImages = await getFromDB("savedImageFiles");
            if (savedImages) {
                setImages(savedImages);
            }
        };

        fetchImages();
    }, []);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [userData, setUserData] = useState(null);
    const [UserLocations, setUserLocations] = useState(null);
    const [tagsArray, setTagsArray] = useState(() => {
        const savedTags = localStorage.getItem("tagsArray");
        return savedTags ? JSON.parse(savedTags) : [];
    });
    const [customTags, setCustomTags] = useState('');
    const [additionalPhones, setAdditionalPhones] = useState([]);
    const [phoneErrors, setPhoneErrors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [volumeUnit, setVolumeUnit] = useState(() => {
        // Initialize with the key directly
        return localStorage.getItem("volumeUnit") || null;
    });
    const [aiSuggestions, setAISuggestions] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successAnnouncementId, setSuccessAnnouncementId] = useState(null);
    const [activeTab, setActiveTab] = useState('advertisement');
    const [jsonData, setJsonData] = useState({});

    const fetchUserData = getUserData(userData, UserLocations, setUserData, setUserLocations);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    useEffect(() => {
        localStorage.setItem("tagsArray", JSON.stringify(tagsArray));
    }, [tagsArray]);

    useEffect(() => {
        localStorage.setItem("selectedLocation", JSON.stringify(selectedLocation));
    }, [selectedLocation]);

    useEffect(() => {
        localStorage.setItem("images", JSON.stringify(images));
    }, [images]);

    useEffect(() => {
        localStorage.setItem("priceType", priceType);
        localStorage.setItem("announcementName", announcementName || "");
        localStorage.setItem("videoLink", videoLink);
        localStorage.setItem("description", description);
        localStorage.setItem("price", price);
    }, [priceType, announcementName, videoLink, description, price]);

    const { handleImagesChange, handleVideoLinkChange, handleAnnouncementInputChange, handleCustomTagsChange, handleSelectCategory, handleDescriptionChange, handleVolumeChange, handleSelectLocation, handleSuggestionClick, handleAIGenerate, handleUpdateSuggestions, cleanup } = changesFun(setAnnouncementName, setVideoLink, setDescription, setSelectedCategory, setSelectedSubcategory, setImages, setSelectedLocation, setCustomTags, setVolumeUnit, setTagsArray, setAISuggestions);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);
    const { handleDeletePhone, handleAddPhone } = phoneFun(setAdditionalPhones, additionalPhones, setPhoneErrors, phoneErrors);

    const handlePublish = async () => {
        const errors = [];

        if (!images || images.length === 0) errors.push(t('required_text'));
        if (!announcementName) errors.push(t('announcementNameError'));
        if (!description) errors.push(t('announcementDescriptionError'));
        if (!price) errors.push(t('announcementPriceError'));
        if (!selectedLocation) errors.push(t('announcementAddressError'));
        if (!tagsArray || tagsArray.length === 0) errors.push(t('tagsArrayError'));
        if (!selectedSubcategory) errors.push(t('announcementCategoryError'));
        if (!volumeUnit) errors.push(t('announcementVolumeUnit'));

        if (errors.length > 0) {
            setErrorMessage(errors.map(error => `<p>${error}</p>`).join(""));
            setShowErrorModal(true);
            setIsSubmitted(true);
            return;
        }

        const result = await publishFun(
            additionalPhones,
            setAdditionalPhones,
            phoneErrors,
            setPhoneErrors,
            setIsSubmitted,
            setIsLoading,
            announcementName,
            description,
            selectedSubcategory,
            selectedLocation,
            videoLink,
            priceType,
            price,
            volumeUnit,
            selectedCategory,
            userData,
            tagsArray,
            images,
            navigate,
            setShowErrorModal,
            showErrorModal,
            setErrorMessage,
            jsonData
        ).handlePublish();
        
        if (result?.announcementId) {
            setSuccessAnnouncementId(result.announcementId);
        }
    };

    const { handleAddTag, handleRemoveTag } = tagsFun(customTags, setTagsArray, tagsArray, setCustomTags);
    const handlePreview = () => {
        const errors = [];

        if (!images || images.length === 0) errors.push(t('required_text'));
        if (!announcementName) errors.push(t('announcementNameError'));
        if (!description) errors.push(t('announcementDescriptionError'));
        if (!price) errors.push(t('announcementPriceError'));
        if (!selectedLocation) errors.push(t('announcementAddressError'));
        if (!tagsArray || tagsArray.length === 0) errors.push(t('tagsArrayError'));
        if (!selectedSubcategory) errors.push(t('announcementCategoryError'));
        if (!volumeUnit) errors.push(t('announcementVolumeUnit'));

        if (errors.length > 0) {
            setErrorMessage(errors.map(error => `<p>${error}</p>`).join(""));
            setShowErrorModal(true);
            setIsSubmitted(true);
            return;
        }

        const convertedVolumeUnit = volumeUnit;

        setErrorMessage("");
        setShowErrorModal(false);

        previewFun(
            announcementName, description, price, priceType, images, videoLink,
            userData, selectedLocation, navigate, setIsLoading,
            additionalPhones, setAdditionalPhones, phoneErrors, setPhoneErrors,
            setIsSubmitted, selectedSubcategory, selectedCategory, tagsArray, convertedVolumeUnit
        );
    };

    const handlePhoneChange = (index, value) => {
        const updatedPhones = [...additionalPhones];
        updatedPhones[index] = value;
        setAdditionalPhones(updatedPhones);

        const updatedErrors = [...phoneErrors];
        updatedErrors[index] = false;
        setPhoneErrors(updatedErrors);
    };

    return (
        AdFormUI(
            handleImagesChange,
            handleVideoLinkChange,
            handleAnnouncementInputChange,
            isSubmitted,
            customTags,
            handleCustomTagsChange,
            handleAddTag,
            handleRemoveTag,
            tagsArray,
            handleSelectCategory,
            handleDescriptionChange,
            priceType,
            setPriceType,
            price,
            setPrice,
            handleVolumeChange,
            handlePublish,
            isLoading,
            handlePreview,
            userData,
            additionalPhones,
            phoneErrors,
            handlePhoneChange,
            handleDeletePhone,
            handleAddPhone,
            selectedLocation,
            UserLocations,
            handleSelectLocation,
            showErrorModal,
            setShowErrorModal,
            errorMessage,
            selectedSubcategory,
            volumeUnit,
            announcementName,
            description,
            aiSuggestions,
            handleSuggestionClick,
            handleAIGenerate,
            handleUpdateSuggestions,
            () => {
                if (successAnnouncementId) {
                    navigate(`/announcement/${successAnnouncementId}`);
                }
            },
            activeTab,
            setActiveTab,
            selectedCategory,
            jsonData,
            setJsonData
        )
    );
}

export default AdForm;